import PropTypes from "prop-types";

const INLINE_TOKEN_REGEX = /(\[.*?\]\(.*?\))|(\*\*.*?\*\*)|(\*.*?\*)|(_.*?_)|(`.*?`)/g;
const LINK_REGEX = /^\[(.*?)\]\((.*?)\)$/;
const BULLET_REGEX = /^(\s*)[-\u2022*]\s+(.+)$/;
const NUM_LIST_REGEX = /^(\s*)\d+\.\s+(.+)$/;
const HEADING_REGEX = /^(#{1,3})\s+(.+)$/;
const BOLD_ONLY_REGEX = /^\*\*([^*]+)\*\*$/;

const makeKey = (prefix, value) =>
  `${prefix}-${String(value || "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40)}`;

const makeUniqueKey = (prefix, value, seenKeys) => {
  const baseKey = makeKey(prefix, value);
  const nextCount = (seenKeys.get(baseKey) || 0) + 1;
  seenKeys.set(baseKey, nextCount);
  return nextCount === 1 ? baseKey : `${baseKey}-${nextCount}`;
};

const parseTableRow = (rowText) => {
  const cells = rowText.split("|").map((cell) => cell.trim());
  if (cells[0] === "") cells.shift();
  if (cells[cells.length - 1] === "") cells.pop();
  return cells;
};

const getHeadingTag = (level) => {
  if (level === 1) return "h1";
  if (level === 2) return "h2";
  return "h3";
};

const renderInlineToken = (token, key) => {
  if (token.startsWith("[") && token.includes("](")) {
    const linkMatch = LINK_REGEX.exec(token);
    if (linkMatch) {
      return (
        <a
          key={key}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="copilot-md__link"
        >
          {linkMatch[1]}
          <i className="pi pi-external-link" style={{ fontSize: "0.65rem", marginLeft: "2px" }} />
        </a>
      );
    }
  }

  if (token.startsWith("**") && token.endsWith("**")) {
    return <strong key={key}>{token.slice(2, -2)}</strong>;
  }

  if ((token.startsWith("*") && token.endsWith("*")) || (token.startsWith("_") && token.endsWith("_"))) {
    return <em key={key}>{token.slice(1, -1)}</em>;
  }

  if (token.startsWith("`") && token.endsWith("`")) {
    return <code key={key} className="copilot-md__inline-code">{token.slice(1, -1)}</code>;
  }

  return <span key={key}>{token}</span>;
};

const renderInlineContent = (text) => {
  const value = String(text || "");
  if (!value) return [];

  const nodes = [];
  let lastIndex = 0;
  INLINE_TOKEN_REGEX.lastIndex = 0;
  let tokenMatch = INLINE_TOKEN_REGEX.exec(value);

  while (tokenMatch) {
    const token = tokenMatch[0];
    const tokenIndex = tokenMatch.index;
    const textBeforeToken = value.slice(lastIndex, tokenIndex);

    if (textBeforeToken) {
      nodes.push(
        <span key={makeKey("text", `${lastIndex}-${textBeforeToken}`)}>
          {textBeforeToken}
        </span>
      );
    }

    nodes.push(renderInlineToken(token, makeKey("inline", `${tokenIndex}-${token}`)));
    lastIndex = tokenIndex + token.length;
    tokenMatch = INLINE_TOKEN_REGEX.exec(value);
  }

  const remainingText = value.slice(lastIndex);
  if (remainingText) {
    nodes.push(
      <span key={makeKey("text", `${lastIndex}-${remainingText}`)}>
        {remainingText}
      </span>
    );
  }

  INLINE_TOKEN_REGEX.lastIndex = 0;
  return nodes;
};

const createBlock = (type, lines, keySeed) => ({
  type,
  lines: [...lines],
  key: makeKey(type, keySeed),
});

const flushOpenBlock = (blocks, currentBlock) => {
  if (!currentBlock || !currentBlock.lines?.length) {
    return { type: null, lines: [], keySeed: "" };
  }

  blocks.push(createBlock(currentBlock.type, currentBlock.lines, currentBlock.keySeed));
  return { type: null, lines: [], keySeed: "" };
};

const startBlock = (blocks, currentBlock, type, keySeed) => {
  flushOpenBlock(blocks, currentBlock);
  return {
    type,
    lines: [],
    keySeed,
  };
};

const addSingleLineBlock = (blocks, currentBlock, block) => {
  flushOpenBlock(blocks, currentBlock);
  blocks.push(block);
  return { type: null, lines: [], keySeed: "" };
};

const appendLineToBlock = (currentBlock, line) => ({
  ...(currentBlock || { type: null, lines: [], keySeed: "" }),
  lines: [...((currentBlock && currentBlock.lines) || []), line],
});

const parseCodeBoundary = (blocks, currentBlock, rawLine, keySeed) => {
  if (currentBlock?.type === "code") {
    return flushOpenBlock(blocks, currentBlock);
  }

  const nextBlock = startBlock(blocks, currentBlock, "code", keySeed);
  return appendLineToBlock(nextBlock, rawLine);
};

const parseTableLine = (blocks, currentBlock, line, keySeed) => {
  const tableBlock =
    currentBlock?.type === "table"
      ? currentBlock
      : startBlock(blocks, currentBlock, "table", keySeed);
  return appendLineToBlock(tableBlock, line);
};

const parseListLine = (blocks, currentBlock, item, keySeed, type) => {
  const listBlock =
    currentBlock?.type === type
      ? currentBlock
      : startBlock(blocks, currentBlock, type, keySeed);
  return appendLineToBlock(listBlock, item);
};

const parseMarkdownBlocks = (text) => {
  const blocks = [];
  let currentBlock = { type: null, lines: [], keySeed: "" };

  String(text || "").split("\n").forEach((rawLine, lineNumber) => {
    const line = rawLine.trim();
    const keySeed = `${lineNumber}-${line || rawLine}`;

    if (line.startsWith("```")) {
      currentBlock = parseCodeBoundary(blocks, currentBlock, rawLine, keySeed);
      return;
    }

    if (currentBlock?.type === "code") {
      currentBlock = appendLineToBlock(currentBlock, rawLine);
      return;
    }

    if (line.startsWith("|") && line.endsWith("|")) {
      currentBlock = parseTableLine(blocks, currentBlock, line, keySeed);
      return;
    }

    if (currentBlock?.type === "table") {
      currentBlock = flushOpenBlock(blocks, currentBlock);
    }

    const bulletMatch = BULLET_REGEX.exec(rawLine);
    if (bulletMatch) {
      currentBlock = parseListLine(blocks, currentBlock, bulletMatch[2], keySeed, "list");
      return;
    }

    if (currentBlock?.type === "list") {
      currentBlock = flushOpenBlock(blocks, currentBlock);
    }

    const numListMatch = NUM_LIST_REGEX.exec(rawLine);
    if (numListMatch) {
      currentBlock = parseListLine(blocks, currentBlock, numListMatch[2], keySeed, "num-list");
      return;
    }

    if (currentBlock?.type === "num-list") {
      currentBlock = flushOpenBlock(blocks, currentBlock);
    }

    if (!line) {
      currentBlock = flushOpenBlock(blocks, currentBlock);
      return;
    }

    const headingMatch = HEADING_REGEX.exec(line);
    if (headingMatch) {
      currentBlock = addSingleLineBlock(blocks, currentBlock, {
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
        key: makeKey("heading", keySeed),
      });
      return;
    }

    const boldOnlyMatch = BOLD_ONLY_REGEX.exec(line);
    if (boldOnlyMatch) {
      currentBlock = addSingleLineBlock(blocks, currentBlock, {
        type: "bold-heading",
        text: boldOnlyMatch[1],
        key: makeKey("bold-heading", keySeed),
      });
      return;
    }

    currentBlock = addSingleLineBlock(blocks, currentBlock, {
      type: "paragraph",
      text: line,
      key: makeKey("paragraph", keySeed),
    });
  });

  flushOpenBlock(blocks, currentBlock);
  return blocks;
};

const renderCodeBlock = (block) => {
  const headerLine = block.lines[0].trim();
  const codeLang = headerLine.replace(/^```/, "").trim();
  const codeText = block.lines.slice(1).join("\n");

  return (
    <div key={block.key} className="copilot-md__code-block-wrapper">
      {codeLang ? <div className="copilot-md__code-lang">{codeLang}</div> : null}
      <pre className="copilot-md__code-block">
        <code>{codeText}</code>
      </pre>
    </div>
  );
};

const renderTableBlock = (block) => {
  const headerCells = parseTableRow(block.lines[0]);
  const dataRows = block.lines.slice(2).map(parseTableRow);
  const rowKeys = new Map();

  return (
    <div key={block.key} className="copilot-md__table-wrapper">
      <table className="copilot-md__table">
        <thead>
          <tr>
            {headerCells.map((cell) => (
              <th key={makeUniqueKey("th", cell, rowKeys)}>{renderInlineContent(cell)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row) => {
            const cellKeys = new Map();
            return (
              <tr key={makeUniqueKey("tr", row.join("|"), rowKeys)}>
                {row.map((cell) => (
                  <td key={makeUniqueKey("td", cell, cellKeys)}>
                    {renderInlineContent(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const renderListBlock = (block, onBulletClick) => {
  const itemKeys = new Map();

  return (
    <ul key={block.key} className="copilot-md__list">
      {block.lines.map((item) => (
        <li key={makeUniqueKey("li", item, itemKeys)}>
          {onBulletClick ? (
            <button
              type="button"
              className="copilot-md__prompt"
              onClick={() => onBulletClick(item)}
            >
              {renderInlineContent(item)}
            </button>
          ) : (
            renderInlineContent(item)
          )}
        </li>
      ))}
    </ul>
  );
};

const renderNumberedListBlock = (block) => {
  const itemKeys = new Map();

  return (
    <ol key={block.key} className="copilot-md__num-list">
      {block.lines.map((item) => (
        <li key={makeUniqueKey("li", item, itemKeys)}>
          {renderInlineContent(item)}
        </li>
      ))}
    </ol>
  );
};

const renderHeadingBlock = (block) => {
  const Tag = getHeadingTag(block.level);
  return (
    <Tag key={block.key} className={`copilot-md__h${block.level}`}>
      {renderInlineContent(block.text)}
    </Tag>
  );
};

const renderMarkdownBlock = (block, onBulletClick) => {
  const renderers = {
    code: renderCodeBlock,
    table: renderTableBlock,
    list: () => renderListBlock(block, onBulletClick),
    "num-list": renderNumberedListBlock,
    heading: renderHeadingBlock,
    "bold-heading": () => (
      <h3 key={block.key} className="copilot-md__h3">
        {block.text}
      </h3>
    ),
    paragraph: () => (
      <p key={block.key} className="copilot-md__p">
        {renderInlineContent(block.text)}
      </p>
    ),
  };

  return renderers[block.type]?.(block) || null;
};

const CopilotMarkdown = ({ text, onBulletClick }) => {
  if (!text) return null;

  const blocks = parseMarkdownBlocks(text);

  return (
    <div className="copilot-md">
      {blocks.map((block) => renderMarkdownBlock(block, onBulletClick))}
    </div>
  );
};

CopilotMarkdown.propTypes = {
  text: PropTypes.string,
  onBulletClick: PropTypes.func,
};

export default CopilotMarkdown;
